import { useState } from "react";
import { Helmet } from "react-helmet";
import {
  useGetHubRegistrationsQuery,
  useGetMeQuery,
  useHubRegistrationMutation,
} from "generated/graphql";
import NavBar from "components/navbar/NavBar";
import SideBar from "components/sidebar/Sidebar";
import TournamentPageWrapper, {
  Tabs,
} from "components/tournament/TournamentPageWrapper";
import Auth from "lib/auth";
import styled from "@emotion/styled";
import InputField, {
  MultiSelectField,
  TextArea,
} from "components/auth/InputField";
import { Error } from "components/auth/shared";

const auth = new Auth();

const HubRegistrationForm = styled.form`
  max-width: 640px;
  margin: 4rem auto;
  input,
  textarea {
    color: #fff;
  }
`;

const PageTitle = styled.h1`
  font-family: Rajdhani, sans-serif;
  font-size: 40px;
  font-weight: bolder;
  text-align: center;
  margin-top: 2rem;
`;

function HubsPage() {
  // Queries and Mutations
  const [activeSection, setActiveSection] = useState<
    "registration" | "registered"
  >("registration");
  const [name, setName] = useState("");
  const [hubLocation, setHubLocation] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [qualifiers, setQualifiers] = useState<
    { [key: string]: string }[] | string
  >("");
  const [website, setWebsite] = useState("");
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined);

  const [
    registrationMutation,
    { loading: registering, error: registrationErr },
  ] = useHubRegistrationMutation();

  const { data, loading } = useGetMeQuery({ nextFetchPolicy: "cache-first" });
  const {
    data: hubsData,
    loading: loadingHubs,
    error: hubsErr,
  } = useGetHubRegistrationsQuery({
    nextFetchPolicy: "cache-first",
  });

  if (hubsErr) {
    auth.clearToken();
    document.location.reload();
  }
  return (
    <TournamentPageWrapper>
      <Helmet>
        <title>Nairobi League Hub Registration — Jiwe eSports</title>
      </Helmet>
      <div className="wrapper">
        <SideBar
          nickname={data?.me?.nickname}
          isLoggedIn={!!data?.me?.nickname}
        />
        {!loading && data && (
          <NavBar nickname={data?.me?.nickname} name={data.me?.name} />
        )}
      </div>

      <div className="container2">
        <div
          className="profile_parent"
          style={{
            maxHeight: 360,
            overflow: "hidden",
          }}
        >
          <img
            className="profile_cover_photo"
            src="https://esports-news.co.uk/wp-content/uploads/2021/05/staffordshire-university-esports-hub.jpg"
            alt=""
          />
          <div className="game_info_pic">
            <img
              alt=""
              src="https://assets.reedpopcdn.com/fifa-main.jpg/BROK/resize/1200x1200%3E/format/jpg/quality/70/fifa-main.jpg"
            />
          </div>
        </div>
      </div>

      <nav className="tourn_float_navbar">
        <button
          onClick={() => setActiveSection("registration")}
          className={activeSection === "registration" ? "active" : undefined}
        >
          Hub Registration
        </button>
        <button
          onClick={() => setActiveSection("registered")}
          className={activeSection === "registered" ? "active" : undefined}
        >
          Registered
        </button>
      </nav>

      <Tabs>
        {activeSection === "registration" && (
          <>
            <PageTitle>Nairobi League Hub Registration</PageTitle>
            <h3
              style={{
                fontFamily: '"Rajdhani", sans-serif',
                maxWidth: 640,
                textAlign: "center",
                margin: "2rem auto 0",
              }}
            >
              This form is for game hubs in Nairobi to register for the Nairobi
              FIFA and F1 league.
            </h3>
            <h3
              style={{
                fontFamily: '"Rajdhani", sans-serif',
                maxWidth: 640,
                textAlign: "center",
                margin: "auto",
              }}
            >
              Each hub that registers will host FIFA and/or F1 qualifiers within
              their area or constituency. With Prizes available for each
              qualifier.
            </h3>
            {(registrationErr || errMsg) && (
              <Error>
                {registrationErr?.message}
                {errMsg}
              </Error>
            )}
            <HubRegistrationForm
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  if (qualifiers.length === 0) {
                    setErrMsg(
                      "You need to select the qualifiers you want to host"
                    );
                    return;
                  }
                  await registrationMutation({
                    variables: {
                      input: {
                        location: hubLocation,
                        name: name,
                        contact_name: contactName,
                        contact_number: contactNumber,
                        website,
                        selected_qualifiers: JSON.stringify(
                          Array.isArray(qualifiers)
                            ? qualifiers.map(({ value }) => value)
                            : []
                        ),
                      },
                    },
                    onQueryUpdated() {
                      setActiveSection("registered");
                      alert("Thanks\nYour submission has been set for approval");
                    },
                    // onCompleted: () => {
                    //   setActiveSection("registered");
                    //   alert("Hub registration complete");
                    // },
                    refetchQueries: ["GetHubRegistrations"],
                  });
                } catch (err) {
                  console.error(err);
                  // @ts-ignore
                  setErrMsg(err);
                }
              }}
            >
              <InputField
                setValue={setName}
                value={name}
                placeholder="Enter hub name"
                type="text"
                name="hub_name"
                label="Hub name"
                className="input-box"
                required={true}
              />
              <br />

              <TextArea
                name="hubLocation"
                setValue={setHubLocation}
                type="text"
                value={hubLocation}
                placeholder=""
                label="Hub location"
                className="input-box"
                required={true}
              />
              <br />

              <MultiSelectField
                setValue={(value: any, action: any) => setQualifiers(value)}
                // @ts-ignore
                value={qualifiers}
                placeholder="Select qualifiers to be played"
                type="text"
                name="qualifiers"
                label="Qualifiers to be played"
                options={[
                  {
                    label: "FIFA 23",
                    value: "FIFA23",
                  },
                  {
                    label: "F1",
                    value: "F1",
                  },
                ]}
              />
              <br />
              <InputField
                setValue={setContactName}
                value={contactName}
                placeholder="Enter contact person name"
                type="text"
                name="contact_name"
                label="Contact person name"
                className="input-box"
                required={true}
              />
              <br />

              <InputField
                setValue={setContactNumber}
                value={contactNumber}
                placeholder="Enter contact person number"
                type="text"
                name="phone_number"
                label="Contact person number"
                className="input-box"
                required={true}
              />
              <br />
              <TextArea
                setValue={setWebsite}
                value={website}
                placeholder="Enter website or social media handles"
                type="text"
                name="website"
                label="Website/Social Media Handles"
                className="input-box"
                required={true}
              />
              {/* <p className="terms">
                    <span>
                      <input
                        type="checkbox"
                        onChange={(event) => setAcknowledged(!acknowledged)}
                        checked={acknowledged}
                      />
                    </span>
                    I accept the Terms of Use and I acknowledge having read the
                    privacy and cookies policy
                  </p> */}
              <button
                type="submit"
                className="btn"
                disabled={loading || registering}
              >
                Register Hub
              </button>
              <br />
            </HubRegistrationForm>
          </>
        )}
        {activeSection === "registered" && !loadingHubs && (
          <div
            style={{
              maxWidth: 960,
              margin: "auto",
            }}
          >
            <table id="leaderboardTable" style={{ marginLeft: 0 }}>
              <thead>
                <tr>
                  <th style={{ color: "#fff", fontWeight: "bold" }}>
                    Name of Hub
                  </th>
                  <th style={{ color: "#fff", fontWeight: "bold" }}>
                    Location
                  </th>
                </tr>
              </thead>
              <tbody>
                {hubsData?.hub_registrations.map((hub) => (
                  <tr key={`hub-${hub.id}`} className="">
                    <td>{hub.name}</td>
                    <td>{hub.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tabs>
    </TournamentPageWrapper>
  );
}

export default HubsPage;
